import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState, {FolderItemStateAttributes} from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import QueryWrapper from 'graphql-client/QueryWrapper';
import Preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';
import {ResultViewType} from 'results/assays/AssayResultsConstants';
import LinkedResultsTabState from 'results/linked-results/LinkedResultsTabState';
import {shouldDisablePrimaryLinkColumn} from 'runs/shouldDisablePrimaryLinkColumn';
import EntityStore from 'stores/EntityStore';
import {resetStudyState} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import StudyDesignTabState from 'studies/workspace/tabs/StudyDesignTabState';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {StudyFileQuery} from './__generated/StudyFileQuery';
import {initializeSearchForItemsInStudy} from './StudySearchHelpers';
import StudyListingTabState from './tabs/StudyListingTabState';
import StudyOverviewTabState from './tabs/StudyOverviewTabState';
import VisualizationState from './tabs/VisualizationState';

type StudyFileGraphQL = ExtractByTypename<StudyFileQuery['study'], 'StudyNode'>;

export const STUDY_FILE_QUERY: DocumentNode = gql`
  query StudyFileQuery($fileId: StrictString!) @engComponent(name: STUDIES) {
    study(id: $fileId) @audit(msg: "Opened study {name}") {
      ... on StudyNode {
        name
        humanId
        studySchema {
          id
          studyType
        }
        ...BaseFolderItemFileGraphQL
      }
      ... on InaccessibleInfoNode {
        ...BaseFolderItemFileGraphQL
      }
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
`;

export class Study extends ApolloFolderItemBase<StudyFileQuery, StudyFileGraphQL> {
  protected getQueryWrapper(): QueryWrapper<StudyFileQuery, StudyFileGraphQL> {
    return {query: STUDY_FILE_QUERY};
  }

  protected queryVariables(): {fileId: string} {
    return {fileId: this.id};
  }

  protected getGraphQLFolderItemFromQueryData(
    data: StudyFileQuery
  ): StudyFileGraphQL | InaccessibleInfoGraphQL {
    return data.study;
  }

  protected getUpdatedQueryData(attrs: Partial<StudyFileGraphQL>): StudyFileQuery {
    return {
      study: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  override projectFolderId(): string {
    return this.folderItem().folder.id;
  }

  override humanId(): string {
    return this.folderItem().humanId;
  }

  studyType(): StudyType {
    return this.folderItem().studySchema.studyType;
  }
}

interface StudyFileState extends FolderItemState {
  model(): Study;
}

class StudyFileState extends FolderItemState {
  static override Model = Study;
  static override fileType = ModelTypeConstants.STUDY;

  static override initClass(): void {
    this.prototype.hasNote = false;
  }

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  override hasMetadata(): boolean {
    return true;
  }

  override hasResults(): boolean {
    const enabledForStudyType = BenchlingConfig.STUDIES_ENABLE_RESULTS[this.model().studyType()] ?? true;
    return (
      enabledForStudyType &&
      isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: this.id}}) &&
      shouldDisablePrimaryLinkColumn() &&
      EntityStore.currentUser()
    );
  }

  hasDesignTab(): boolean {
    return getTypedStudyAdapter(this.model().studyType()).design.ui.tab != null;
  }

  hasVisualizationTab(): boolean {
    return getTypedStudyAdapter(this.model().studyType()).isVisualizationEnabled;
  }

  override initialize(attributes: FolderItemStateAttributes, options: unknown): void {
    super.initialize(attributes, options);
    const overviewTab = new StudyOverviewTabState({studyId: this.id});
    initializeSearchForItemsInStudy(this.id);
    const listingTab = new StudyListingTabState({studyId: this.id, studyType: this.model().studyType()});
    this.buffers().at(0).tabs().add(overviewTab);
    this.buffers().at(1).tabs().add(listingTab);
    if (this.hasDesignTab()) {
      const designTab = new StudyDesignTabState({studyId: this.id, studyType: this.model().studyType()});
      this.buffers().at(0).tabs().add(designTab);
    }
    if (this.hasVisualizationTab()) {
      const visualizationTab = new VisualizationState({studyId: this.id});
      this.buffers().at(0).tabs().add(visualizationTab);
    }
    if (this.hasResults()) {
      this.buffers()
        .at(1)
        .tabs()
        .add(
          new LinkedResultsTabState({
            fileId: this.id,
            filters: {studyId: this.id},
            resultType: ResultViewType.STUDY,
          })
        );
    }
    this.buffers().at(1).tabs().add(this.metadataTab);
    overviewTab.set({active: true});
    const splitPref = Preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : false);
  }

  override cleanUp(): void {
    super.cleanUp();
    // Clear persisted per-study run/plan UI state when the study workspace item itself is closed.
    getStore().dispatch(resetStudyState(this.id));
  }
}

StudyFileState.initClass();

export default StudyFileState;
